import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaDownload, FaUser, FaEnvelope, FaPhone, FaLock, FaEye, FaEyeSlash, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { authAPI } from '../../api';
import './AbstractSubmission.css';

const AbstractSubmission = () => {
  const [user, setUser] = useState(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    lastName: '',
    nameInitials: '',
    username: '',
    email: '',
    phone: '',
    affiliation: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loggedInUser = localStorage.getItem('user');
    if (loggedInUser) {
      setUser(JSON.parse(loggedInUser));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const titles = ['Prof', 'Dr', 'Mr', 'Mrs', 'Ms'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.title) return 'Please select a title';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.nameInitials.trim()) return 'First name is required';
    if (!formData.username.trim()) return 'Username is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) return 'Please enter a valid 10-digit phone number';
    if (!formData.affiliation.trim()) return 'Affiliation is required';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    
    const result = await authAPI.register(formData);
    setIsLoading(false);

    if (result.success) {
      setShowSignupModal(false);
      navigate('/registration-success');
    } else {
      setError(result.message || 'Registration failed. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowSignupModal(false);
    setFormData({
      title: '',
      lastName: '',
      nameInitials: '',
      username: '',
      email: '',
      phone: '',
      affiliation: '',
      password: '',
      confirmPassword: ''
    });
    setError('');
  };

  return (
    <div className="abstract-submission-page">
      <div className="abstract-submission-container">
        <h1 className="abstract-submission-title"> Extended Abstract Submission</h1>
        
        <div className="submission-content">
          <section className="guidelines">
            <h2>Submission Guidelines</h2>
            <ul>
              <li>The extended abstract should not exceed one page.</li>
              <li>Follow the Template regarding font style, size, and alignment while preparing the abstract.</li>
              <li>Convert The word file into pdf (not exceeding 2MB) before submission.</li>
              <li>Register by Clicking on the "Register" button.</li>
              <li>Login to submit the abstract..</li>
            </ul>
          </section>

          <div className="template-download-card">
            <div className="template-card-header">
              <FaDownload className="template-icon" />
              <h3>Download Template</h3>
            </div>
            <p className="template-description">
              Download the abstract template in Microsoft Word format to prepare your submission according to conference standards.
            </p>
            <a 
              href="/AbstractTemplate.docx" 
              download="AbstractTemplate.docx"
              className="abstract-submission-btn abstract-submission-btn-download"
            >
              <FaDownload /> Download DOCX
            </a>
          </div>

          <div className="action-card combined-action-card">
            {user ? (
              <>
                <h3>Welcome, {user.username}!</h3>
                <p>You are logged in. Go to your dashboard to submit your abstract.</p>
                <div className="action-buttons-group">
                  <Link to="/dashboard" className="abstract-submission-btn abstract-submission-btn-primary">
                    Go to Dashboard
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="abstract-logout-btn"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Ready to Submit?</h3>
                <p>Create an account or login to upload your abstract for the conference.</p>
                <div className="action-buttons-group">
                  <button 
                    onClick={() => setShowSignupModal(true)}
                    className="abstract-submission-btn abstract-submission-btn-primary"
                  >
                    Signup
                  </button>
                  <span className="or-divider">OR</span>
                  <Link to="/login" className="abstract-submission-btn abstract-submission-btn-secondary">
                    Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="abstract-signup-modal-overlay">
          <div className="abstract-signup-modal">
            <div className="abstract-signup-modal-header">
              <h2>Create Your Account</h2>
              <button 
                onClick={handleCloseModal}
                className="abstract-signup-modal-close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="abstract-signup-modal-body">
              {error && (
                <div className="abstract-signup-error-message">
                  <FaExclamationTriangle className="error-icon" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="abstract-signup-form">
                {/* Title */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-title" className="abstract-signup-form-label">
                    <FaUser /> Title
                  </label>
                  <select
                    id="signup-title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="abstract-signup-form-control"
                  >
                    <option value="">Select Title</option>
                    {titles.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Name Initials and Last Name - Side by side */}
                <div className="abstract-signup-form-row">
                  <div className="abstract-signup-form-group">
                    <label htmlFor="signup-nameInitials" className="abstract-signup-form-label">
                      <FaUser /> First Name
                    </label>
                    <input
                      type="text"
                      id="signup-nameInitials"
                      name="nameInitials"
                      value={formData.nameInitials}
                      onChange={handleChange}
                      className="abstract-signup-form-control"
                      placeholder="e.g., A.B.C"
                      maxLength="10"
                    />
                  </div>

                  <div className="abstract-signup-form-group">
                    <label htmlFor="signup-lastName" className="abstract-signup-form-label">
                      <FaUser /> Last Name
                    </label>
                    <input
                      type="text"
                      id="signup-lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="abstract-signup-form-control"
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                {/* Affiliation */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-affiliation" className="abstract-signup-form-label">
                    <FaUser /> Affiliation
                  </label>
                  <input
                    type="text"
                    id="signup-affiliation"
                    name="affiliation"
                    value={formData.affiliation}
                    onChange={handleChange}
                    className="abstract-signup-form-control"
                    placeholder="e.g., University/Organization name"
                  />
                </div>

                {/* Email */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-email" className="abstract-signup-form-label">
                    <FaEnvelope /> Email Address
                  </label>
                  <input
                    type="email"
                    id="signup-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="abstract-signup-form-control"
                    placeholder="Enter your email"
                  />
                </div>

                {/* Phone */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-phone" className="abstract-signup-form-label">
                    <FaPhone /> Phone Number
                  </label>
                  <input
                    type="tel"
                    id="signup-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="abstract-signup-form-control"
                    placeholder="Enter 10-digit phone number"
                  />
                </div>

                {/* Username */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-username" className="abstract-signup-form-label">
                    <FaUser /> Username
                  </label>
                  <input
                    type="text"
                    id="signup-username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="abstract-signup-form-control"
                    placeholder="Choose a username"
                  />
                </div>

                {/* Password */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-password" className="abstract-signup-form-label">
                    <FaLock /> Password
                  </label>
                  <div className="abstract-signup-password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="signup-password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="abstract-signup-form-control"
                      placeholder="Enter password (min 6 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="abstract-signup-password-toggle"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="abstract-signup-form-group">
                  <label htmlFor="signup-confirmPassword" className="abstract-signup-form-label">
                    <FaLock /> Confirm Password
                  </label>
                  <div className="abstract-signup-password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="signup-confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="abstract-signup-form-control"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="abstract-signup-password-toggle"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="abstract-signup-btn-submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbstractSubmission;
